import React from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { api } from "../../../services";
import Grid from "@material-ui/core/Grid";
import { CustomText } from "../../../components/index";
import ChechkInAndOut from "./ChechkInAndOut";
import masks from "../../../utils/masks";
import hooks from "../../../utils/hooks";
import validations from "../../../utils/validations";
import { customModal } from "../../../components/modals/utils";
import Styles from "../styles";
import alerts from "../../../utils/alerts";
import {
  Input,
  InputTags,
  InputPhone,
  Select,
} from "../../../components/inputs/newForm";

function normalizeDate(val: any): Date | undefined {
  if (!val) return undefined;
  if (typeof val === "string") {
    const m = val.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (m) return new Date(parseInt(m[1]), parseInt(m[2]) - 1, parseInt(m[3]));
  }
  if (val instanceof Date) return val;
  return undefined;
}

const serviceProviderSchema = z.object({
  prestador: z.string()
    .min(1, "*Campo Obrigatório"),
  property: z
    .array(z.object({ value: z.string(), label: z.string() }))
    .min(1, "*Campo Obrigatório"),
  cpf: z.string()
    .min(1, "*Campo Obrigatório")
    .refine(
      (val) => validations.validateCPF(val) === null,
      { message: "CPF inválido" }
    ),
  email: z.string().min(1, "*Campo Obrigatório").email("E-mail inválido"),
  telefone: z.string().min(1, "*Campo Obrigatório"),
  startDate: z.date().nullable().refine((val) => val !== null, { message: "*Campo Obrigatório" }),
  endDate: z.date().nullable().refine((val) => val !== null, { message: "*Campo Obrigatório" }),
  checkInTime: z.string().min(1, "*Campo Obrigatório"),
  checkOutTime: z.string().min(1, "*Campo Obrigatório"),
});

type ServiceProviderFormData = z.infer<typeof serviceProviderSchema>;

export function GridRegisterServiceProvider(props) {
  const { edit, oldData, serviceProviderList, reload, page, onImageUrlFetch } =
    props;

  const { loading, call } = hooks.useRequest();
  const [unError, setUnError] = React.useState<any>(false);
  const [timeError, setTimeError] = React.useState<any>(false);
  const [hourError, setHourError] = React.useState<any>(false);
  const [warning, setWarning] = React.useState<any>(false);

  const [placeList, setPlaceList] = React.useState<any>([]);

  const propertyOptions = React.useMemo(
    () =>
      oldData
        ? oldData?.places?.map((i: any) => ({ value: i?.id ?? "", label: i?.name ?? "" })) ?? []
        : placeList,
    [oldData, placeList]
  );
  const { control, handleSubmit, setValue, getValues, watch } =
    useForm({
      resolver: zodResolver(serviceProviderSchema),
      defaultValues: {
        prestador: oldData?.primaryGuest?.id ?? "",
        property: oldData?.places?.map((i) => ({ value: i?.id ?? "", label: i?.name ?? "" })) ?? [],
        cpf: oldData?.primaryGuest?.document || "",
        email: oldData?.primaryGuest?.email || "",
        telefone: oldData?.primaryGuest?.phone || "",
        startDate: normalizeDate(oldData?.startDate),
        endDate: normalizeDate(oldData?.endDate),
        checkInTime: oldData ? masks.extractHour(oldData?.startDate) : "",
        checkOutTime: oldData ? masks.extractHour(oldData?.endDate) : "",
      },
    });




  React.useEffect(() => {
    if (oldData) {
      handleServiceProvider(oldData?.primaryGuest?.id);
    }

  }, [oldData]);

  const handleServiceProvider = (serviceProviderid: string) => {
    if (!serviceProviderid) return;
    const selectedProvider = serviceProviderList?.find(
      (p: any) => p.id === serviceProviderid
    );
    console.log(selectedProvider, serviceProviderList)
    const placesToSelect = selectedProvider?.places.map((place: any) => ({
      label: place.name,
      value: place.id,
    }));
    if (selectedProvider) {
      setPlaceList(placesToSelect || []);
      setValue("cpf", selectedProvider.document || oldData?.primaryGuest?.document || "");
      setValue("email", selectedProvider.email || oldData?.primaryGuest?.email || "");
      setValue("telefone", selectedProvider.phone || oldData?.primaryGuest?.phone || "");
    } else {
      console.warn("Provider não encontrado no serviceProviderList.");
    }
  };

  const findFirstValidFacial = (renters: any[]) => {
    if (!renters || renters.length === 0) return null;
    for (let i = renters.length - 1; i >= 0; i--) {
      if (renters[i].imageId) return renters[i];
    }
    alerts.alertError("Nenhum facial encontrado");
    return null;
  };

  const fetchImageById = (imageId: string) => {
    if (!imageId) return;
    try {
      call(null, api.getImageDownload(imageId), (response) => {
        if (response?.ok && response?.data) {
          onImageUrlFetch({ imageId, facialUrl: response.data });
        }
      });
    } catch (e) {
      console.log("failed to fetch image", e);
    }
  };

  const handelSubmit = (data: ServiceProviderFormData) => {
    const startDate = masks.dateToSubmit(data.startDate) + " " + data?.checkInTime;
    const endDate = masks.dateToSubmit(data.endDate) + " " + data?.checkOutTime;

    if (oldData) {
      const selectedProvider = serviceProviderList?.find(
        (p: any) => p.id === data.prestador
      );
      const putData = {
        id: oldData?.id,
        startDate,
        endDate, numGuests: 0,
        primaryGuest: {
          id: data.prestador,
          name: selectedProvider?.responsibleName ?? oldData?.primaryGuest?.name,
          document: masks.removeNumberMask(data.cpf) || oldData?.primaryGuest?.document,
          birthDate: oldData?.primaryGuest?.birthDate,
          email: data.email || oldData?.primaryGuest?.email,
          phone: masks.removeNumberMask(data.telefone) || oldData?.primaryGuest?.phone,
          vehiclePlate: oldData?.primaryGuest?.vehiclePlate,
        },
      };

      call(null, api.putServiceProviderReservation(putData as any), (response) => {
        if (response.ok) {
          customModal.close();
          reload(page);
          alerts.setErrorModal(
            "Reserva Atualizada",
            ["Reserva atualizada com sucesso!"],
            null,
            "Ok, Entendi",
            "#0C396D"
          );
        }
      });
    } else {
      const filteredPlaces = data?.property
        ?.filter((obj) => obj.value !== "all")
        .map((obj) => obj.value);
      const submitData = {
        id: data.prestador,
        placeIds: filteredPlaces,
        startDate,
        endDate,
      };

      call(null, api.postServiceProviderReservation(submitData), (response) => {
        if (response.ok) {
          customModal.close();
          reload(page);
          alerts.setErrorModal(
            "Reserva Cadastrada",
            ["Reserva cadastrada com sucesso!"],
            null,
            "Ok, Entendi",
            "#0C396D"
          );
        }
      });
    }
  };

  return (
    <>
      <Grid item xs={12} md={12} lg={3}>
        <Select
          readOnly={oldData}
          control={control}
          name="prestador"
          defaultValue={oldData?.primaryGuest?.id ?? ""}
          label="Selecionar Prestador de Serviço"
          options={oldData ? [{ value: oldData?.primaryGuest?.id, label: oldData?.primaryGuest?.name }] : serviceProviderList?.map((item: any) => ({
            label: item.responsibleName,
            value: item.id,
          }))}
          onChangeCustom={(val) => {
            if (val) {
              handleServiceProvider(val);
              setValue("property", []);
              onImageUrlFetch(null, true);
            }
          }}
        />
      </Grid>

      <Grid item xs={12} md={12} lg={3}>
        <InputTags
          control={control}
          name="property"
          label="Imóvel"
          readOnly={oldData}
          options={propertyOptions}
          multiple={true}
        />
      </Grid>

      <Grid item xs={12} md={12} lg={3}>
        <Input
          control={control}
          name="cpf"
          label="CPF"
          mask={masks.CPF}
          onBlur={() => {
            const cpfVal = getValues("cpf");
            if (!cpfVal) return;
            const cpf = masks.removeNumberMask(cpfVal);
            if (cpf.length === 11) {
              call(null, api.validadeCPF(cpf), async (response) => {
                if (response?.ok && response?.data?.length > 0) {
                  const renterWithImage = findFirstValidFacial(response.data);
                  if (renterWithImage?.imageId) {
                    fetchImageById(renterWithImage.imageId);
                  }
                }
              });
            }
          }}
        />
      </Grid>

      <ChechkInAndOut
        control={control}
        watch={watch}
        setValue={setValue}
        getValues={getValues}
        oldData={oldData}
        edit={edit}
      />

      <Grid item xs={12} md={12} lg={3}>
        <Input control={control} name="email" label="E-mail" />
      </Grid>

      <Grid item xs={12} md={12} lg={3}>
        <InputPhone control={control} name="telefone" label="Telefone" />
      </Grid>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "12px",
          paddingTop: "12px",
          width: "100%",
        }}
      >
        <CustomText
          style={{ whiteSpace: "pre-wrap" }}
          fontSize={1.75}
          textColor="#ff7733"
        >
          {unError && "*Campos Obrigatórios \n"}
          {timeError && timeError + "\n"}
          {hourError && "*" + hourError}
          {warning && "*" + warning}
        </CustomText>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            gap: "12px",
          }}
        >
          <Styles.CancelButton
            loading={loading}
            disabled={loading}
            onClick={() => customModal.close()}
            fullWidth={false}
          >
            Cancelar
          </Styles.CancelButton>

          <Styles.RegitryButton
            fullWidth={false}
            loading={loading}
            disabled={loading}
            onClick={() => {
              handleSubmit(
                (data) => { setUnError(false); handelSubmit(data); },
                () => setUnError(true)
              )();
            }}
          >
            Salvar
          </Styles.RegitryButton>
        </div>
      </div>
    </>
  );
}
